/*
 * Data source adapter.
 *
 * Two implementations behind one call:
 *   - mock  — the seeded generator, used whenever no API base URL is set.
 *   - rest  — GET {VITE_API_BASE_URL}/v1/snapshot?range=…, expected to return
 *             a `Snapshot` verbatim (see docs/INGEST_API.md).
 *
 * Set VITE_API_BASE_URL in a .env file to switch. If the REST call fails the
 * dashboard falls back to mock data and flags it, rather than showing an empty
 * page — a dev looking at this should always see the layout.
 */

import { API_BASE, isLiveConfigured } from './api';
import { authHeaders } from './auth';
import { generateSnapshot } from './mock';
import type { RangeKey, Snapshot } from './types';

/*
 * Authorization comes from `auth.ts` — a session token minted by the Worker
 * when the operator signs in, held in this browser only.
 *
 * Deliberately *not* a build-time env var: a Vite variable is compiled into the
 * bundle, so shipping a credential that way publishes it to anyone who opens
 * the page. The deployed files contain no secret at all, which is what lets the
 * site sit on a public URL without handing out API access.
 */

/** `unauthorized` means the API is reachable but the session is missing or stale. */
export type SourceKind = 'mock' | 'live' | 'fallback' | 'unauthorized';

export interface FetchResult {
  snapshot: Snapshot;
  source: SourceKind;
  /** Populated when a live fetch was attempted and failed. */
  error?: string;
}

/** Small delay so loading states are exercised in dev rather than never seen. */
const MOCK_LATENCY_MS = 180;

export async function fetchSnapshot(range: RangeKey, signal?: AbortSignal): Promise<FetchResult> {
  if (!API_BASE) {
    await new Promise((resolve) => setTimeout(resolve, MOCK_LATENCY_MS));
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    return { snapshot: generateSnapshot(range), source: 'mock' };
  }

  try {
    const res = await fetch(`${API_BASE}/v1/snapshot?range=${range}`, {
      signal,
      headers: { Accept: 'application/json', ...authHeaders() },
    });
    // Distinguished from a network failure: the service is up and answering,
    // it just wants a signed-in caller. Showing sample data here would be
    // misleading, so the app turns this into the sign-in screen.
    if (res.status === 401) {
      return { snapshot: generateSnapshot(range), source: 'unauthorized' };
    }
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    const snapshot = (await res.json()) as Snapshot;
    return { snapshot: { ...snapshot, isMock: false }, source: 'live' };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    return {
      snapshot: generateSnapshot(range),
      source: 'fallback',
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

export { isLiveConfigured };
